// -----------------------------------------------------------------------------
// スクロール時のフェード表示
// -----------------------------------------------------------------------------
/**
 * スクロール位置に応じてフェード対象へ表示クラスを付け外しします。
 * @param {IntersectionObserverEntry[]} entries 画面との交差状態が変わった要素
 * @returns {void}
 */
function update_fade_visibility(entries) {
  for (const entry of entries) {
    if (!(entry.target instanceof HTMLElement)) {
      continue;
    }

    entry.target.classList.toggle("is-active", entry.isIntersecting);
  }
}

const fade_targets = document.querySelectorAll(".lp-famite__fade");

if ("IntersectionObserver" in window) {
  const fade_observer = new IntersectionObserver(update_fade_visibility);

  for (const target of fade_targets) {
    fade_observer.observe(target);
  }
} else {
  for (const target of fade_targets) {
    target.classList.add("is-active");
  }
}

// -----------------------------------------------------------------------------
// サイズ選択と価格画像の切り替え
// -----------------------------------------------------------------------------
const famite_price_image_transitions = new WeakMap();

/**
 * 選択サイズに合わせて価格画像とボタン状態を更新します。
 * @param {HTMLButtonElement} selected_button 選択されたサイズボタン
 * @returns {void}
 */
function select_famite_price(selected_button) {
  const price_url = selected_button.dataset.priceUrl;
  const cv_section = selected_button.closest(".lp-famite__cv");

  if (!price_url || !(cv_section instanceof HTMLElement)) {
    return;
  }

  const price_image = cv_section.querySelector("[data-famite-price-image]");
  if (!(price_image instanceof HTMLImageElement)) {
    return;
  }

  const size_buttons = cv_section.querySelectorAll("[data-famite-size]");
  for (const button of size_buttons) {
    if (button instanceof HTMLButtonElement) {
      button.setAttribute("aria-pressed", String(button === selected_button));
    }
  }

  const transition_state = famite_price_image_transitions.get(price_image) || { id: 0, timer_id: null };
  window.clearTimeout(transition_state.timer_id);
  transition_state.id += 1;
  famite_price_image_transitions.set(price_image, transition_state);

  const transition_id = transition_state.id;
  const price_alt = selected_button.dataset.priceAlt || "選択したサイズのクーポン適用価格";
  const prefers_reduced_motion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const current_price_url = price_image.getAttribute("src");

  if (prefers_reduced_motion || current_price_url === price_url) {
    price_image.classList.remove("is-changing");
    price_image.src = price_url;
    price_image.alt = price_alt;
    return;
  }

  price_image.classList.add("is-changing");
  transition_state.timer_id = window.setTimeout(() => {
    if (transition_state.id !== transition_id) {
      return;
    }

    const reveal_price_image = () => {
      if (transition_state.id !== transition_id) {
        return;
      }

      price_image.classList.remove("is-changing");
      transition_state.timer_id = null;
    };

    price_image.addEventListener("load", reveal_price_image, { once: true });
    price_image.addEventListener("error", reveal_price_image, { once: true });
    price_image.src = price_url;
    price_image.alt = price_alt;
  }, 180);
}

const famite_size_buttons = document.querySelectorAll(".lp-famite__cv [data-famite-size]");
for (const button of famite_size_buttons) {
  if (button instanceof HTMLButtonElement) {
    button.addEventListener("click", () => select_famite_price(button));
  }
}

// -----------------------------------------------------------------------------
// クーポンコードのコピーと結果表示
// -----------------------------------------------------------------------------
const famite_copy_timers = new WeakMap();

/**
 * コピー結果を3秒間表示し、再表示時は残り時間をリセット
 *
 * @param {HTMLElement} copy_status 結果を表示する吹き出し
 * @param {string} message コピー結果のメッセージ
 * @returns {void}
 */
function show_famite_copy_status(copy_status, message) {
  window.clearTimeout(famite_copy_timers.get(copy_status));
  copy_status.textContent = message;
  copy_status.classList.add("is-visible");
  const timer_id = window.setTimeout(() => {
    copy_status.classList.remove("is-visible");
    window.setTimeout(() => {
      if (!copy_status.classList.contains("is-visible")) {
        copy_status.textContent = "";
      }
    }, 250);
    famite_copy_timers.delete(copy_status);
  }, 3000);
  famite_copy_timers.set(copy_status, timer_id);
}

/**
 * Famiteクーポンコードをコピーし、結果を一時表示する
 *
 * @param {HTMLButtonElement} button クーポンコードを持つボタン
 * @returns {Promise<void>} コピー処理の完了
 */
async function copy_famite_coupon_code(button) {
  const coupon_code = button.dataset.famiteCopyCode;
  const cv_section = button.closest(".lp-famite__cv");
  const copy_status = cv_section?.querySelector("[data-famite-copy-status]");

  if (!coupon_code) {
    return;
  }

  try {
    await navigator.clipboard.writeText(coupon_code);
    if (copy_status instanceof HTMLElement) {
      show_famite_copy_status(copy_status, "クーポンコードをコピーしました");
    }
  } catch (error) {
    if (copy_status instanceof HTMLElement) {
      show_famite_copy_status(copy_status, `コピーできませんでした。クーポンコード：${coupon_code}`);
    }
  }
}

const famite_coupon_buttons = document.querySelectorAll(".lp-famite__cv [data-famite-copy-code]");
for (const button of famite_coupon_buttons) {
  if (button instanceof HTMLButtonElement) {
    button.addEventListener("click", () => copy_famite_coupon_code(button));
  }
}

// -----------------------------------------------------------------------------
// Visumoの「もっと見る」リンク追加
// -----------------------------------------------------------------------------
/**
 * Visumoが生成したリンク枠に、重複しないよう固定の一覧リンクを追加
 *
 * @param {HTMLElement} visumo_root このLPのVisumo表示領域
 * @returns {void}
 */
function insert_famite_visumo_link(visumo_root) {
  const link_slots = visumo_root.querySelectorAll(".msm-visumo-pro__wrapper .msm-visumo-pro__list-link");
  for (const link_slot of link_slots) {
    if (link_slot.querySelector(".msm-visumo-pro__list-link-btn")) {
      continue;
    }

    const link_button = document.createElement("a");
    link_button.href = "/pages/instagram-item-post?vsmtag_key=6689865564259";
    link_button.className = "msm-visumo-pro__list-link-btn";
    link_button.textContent = "もっと見る";
    link_slot.appendChild(link_button);
  }
}

const famite_visumo_root = document.querySelector(".lp-famite__visumo-content #msm-visumo-pro");
if (famite_visumo_root instanceof HTMLElement) {
  insert_famite_visumo_link(famite_visumo_root);
  const famite_visumo_observer = new MutationObserver(() => insert_famite_visumo_link(famite_visumo_root));
  famite_visumo_observer.observe(famite_visumo_root, { childList: true, subtree: true });
}

// -----------------------------------------------------------------------------
// FV通過後の追従CVボタン
// -----------------------------------------------------------------------------
const famite_sticky_cv = document.querySelector("[data-famite-sticky-cv]");
const famite_hero = document.querySelector(".lp-famite__hero");
const famite_story_cv_target = document.getElementById("lp-famite-cv-size-story");

/**
 * FV通過後からstory CVが画面に入るまで追従CVボタンを表示
 *
 * @returns {void}
 */
function update_famite_sticky_cv_visibility() {
  const has_passed_hero = famite_hero.getBoundingClientRect().bottom <= 0;
  const has_reached_story_cv = famite_story_cv_target.getBoundingClientRect().top <= window.innerHeight;
  famite_sticky_cv.classList.toggle("is-visible", has_passed_hero && !has_reached_story_cv);
}

if (
  famite_sticky_cv instanceof HTMLElement &&
  famite_hero instanceof HTMLElement &&
  famite_story_cv_target instanceof HTMLElement
) {
  update_famite_sticky_cv_visibility();

  if ("IntersectionObserver" in window) {
    const famite_sticky_cv_observer = new IntersectionObserver(update_famite_sticky_cv_visibility);
    famite_sticky_cv_observer.observe(famite_hero);
    famite_sticky_cv_observer.observe(famite_story_cv_target);
  } else {
    window.addEventListener("scroll", update_famite_sticky_cv_visibility, { passive: true });
  }
}
